import type { ConsentTemplateInput } from "../../../api/models/consentModels";
import type { InputActor, InputBinding, InputDefinitions, InputKind, PuckBlock, TemplateData } from "./types";
import { ConsentTemplateInputType } from "./types";
import { allInputs, findInputDefinition } from "./variables";

export type TemplateBuilderState = { data: TemplateData; inputDefinitions: InputDefinitions };
export type TemplateBuilderCommand =
  | { type: "replaceData"; data: TemplateData }
  | { type: "replaceState"; state: TemplateBuilderState }
  | { type: "ensureInsertedInput"; actor: InputActor; inputId: string; kind: InputKind }
  | { type: "updateInput"; actor: InputActor; inputId: string; update: Partial<ConsentTemplateInput> }
  | { type: "moveInput"; input: InputBinding; actor: InputActor }
  | { type: "renameInput"; actor: InputActor; inputId: string; label: string };

export const inputKindForBlock = (type: keyof import("./types").TemplateComponents): InputKind | undefined => {
  if (type === "TextInput") return "text";
  if (type === "TextArea") return "textarea";
  if (type === "Checkbox") return "checkbox";
  if (type === "CheckboxGroup") return "choice";
  return type === "RadioInput" ? "radio" : undefined;
};

export const inputDefaults = (kind: InputKind): Omit<ConsentTemplateInput, "id"> => {
  if (kind === "textarea") return { label: "New text area", type: ConsentTemplateInputType.TEXT, multiline: true };
  if (kind === "checkbox") return { label: "New checkbox", type: ConsentTemplateInputType.CHECKBOX };
  if (kind === "radio") return { label: "New radio input", type: ConsentTemplateInputType.RADIO, options: [{ id: "option_1", label: "Option 1" }] };
  if (kind === "choice") return { label: "New checkbox group", type: ConsentTemplateInputType.CHECKBOX_GROUP, options: [{ id: "option_1", label: "Option 1" }] };
  return { label: "New text input", type: ConsentTemplateInputType.TEXT };
};

export const generatedInputId = (blockId: unknown) => {
  const value = String(blockId ?? "");
  return value ? `input_${value.replace(/[^A-Za-z0-9_-]/g, "_")}` : undefined;
};

const uniqueLabel = (label: string, definitions: InputDefinitions) => {
  const labels = new Set(allInputs(definitions).map((input) => input.label.trim().toLocaleLowerCase()));
  let candidate = label;
  for (let suffix = 2; labels.has(candidate.toLocaleLowerCase()); suffix += 1) candidate = `${label} ${suffix}`;
  return candidate;
};

export const slugForLabel = (label: string) => {
  const slug = label.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  return (/^[a-z]/.test(slug) ? slug : `input_${slug || "field"}`).slice(0, 100);
};

export const uniqueInputId = (label: string, definitions: InputDefinitions, exclude?: InputBinding) => {
  const ids = new Set(allInputs(definitions)
    .filter((item) => item.actor !== exclude?.actor || item.id !== exclude.inputId)
    .map((item) => item.id));
  const base = slugForLabel(label);
  let candidate = base;
  for (let suffix = 2; ids.has(candidate); suffix += 1) {
    candidate = `${base.slice(0, Math.max(1, 100 - String(suffix).length - 1))}_${suffix}`;
  }
  return candidate;
};

export const referencedInputIds = (data: TemplateData) => {
  const references = new Set<string>();
  const visit = (block: PuckBlock) => {
    const input = block.props.input as Partial<InputBinding> | undefined;
    if ((input?.actor === "employee" || input?.actor === "family") && input.inputId) references.add(`${input.actor}:${input.inputId}`);
    if (Array.isArray(block.props.content)) block.props.content.forEach((child) => visit(child as PuckBlock));
  };
  (data.content as PuckBlock[]).forEach(visit);
  Object.values(data.zones ?? {}).forEach((blocks) => blocks.forEach(visit));
  return references;
};

const mapEditorBlocks = (data: TemplateData, map: (block: PuckBlock) => PuckBlock): TemplateData => ({
  ...data,
  content: (data.content as PuckBlock[]).map(map) as TemplateData["content"],
  zones: data.zones && Object.fromEntries(Object.entries(data.zones).map(([zone, blocks]) => [zone, blocks.map(map)])) as TemplateData["zones"],
});

const renameReferences = (block: PuckBlock, actor: InputActor, oldId: string, nextId: string, nextActor = actor): PuckBlock => {
  const props = { ...block.props };
  const input = props.input as Partial<InputBinding> | undefined;
  if (input?.actor === actor && input.inputId === oldId) props.input = { actor: nextActor, inputId: nextId };
  if (Array.isArray(props.content)) props.content = props.content.map((child) => renameReferences(child as PuckBlock, actor, oldId, nextId, nextActor));
  return { ...block, props };
};

const createDefinitionsForInsertedBlocks = (data: TemplateData, current: InputDefinitions) => {
  let definitions = current;
  const visit = (block: PuckBlock): PuckBlock => {
    const kind = inputKindForBlock(block.type);
    const props = { ...block.props };
    const input = props.input as Partial<InputBinding> | undefined;
    const actor: InputActor = input?.actor === "employee" ? "employee" : "family";
    const id = generatedInputId(props.id);
    if (kind && id && (!input?.inputId || input.inputId.startsWith("__new_") || input.inputId === id)) {
      if (!findInputDefinition(definitions, actor, id)) {
        const defaults = inputDefaults(kind);
        definitions = { ...definitions, [actor]: definitions[actor].concat({ ...defaults, label: uniqueLabel(defaults.label, definitions), id }) };
      }
      props.input = { actor, inputId: id };
    }
    if (Array.isArray(props.content)) props.content = props.content.map((child) => visit(child as PuckBlock));
    return { ...block, props };
  };
  return { data: mapEditorBlocks(data, visit), definitions };
};

const pruneDeletedReferences = (previous: TemplateBuilderState, next: TemplateBuilderState): TemplateBuilderState => {
  const before = referencedInputIds(previous.data);
  const after = referencedInputIds(next.data);
  return {
    ...next,
    inputDefinitions: {
      employee: next.inputDefinitions.employee.filter((item) => !before.has(`employee:${item.id}`) || after.has(`employee:${item.id}`)),
      family: next.inputDefinitions.family.filter((item) => !before.has(`family:${item.id}`) || after.has(`family:${item.id}`)),
    },
  };
};

export const templateBuilderReducer = (state: TemplateBuilderState, command: TemplateBuilderCommand): TemplateBuilderState => {
  if (command.type === "replaceState") return command.state;
  if (command.type === "replaceData") {
    const created = createDefinitionsForInsertedBlocks(command.data, state.inputDefinitions);
    return pruneDeletedReferences(state, { data: created.data, inputDefinitions: created.definitions });
  }
  if (command.type === "ensureInsertedInput") {
    if (findInputDefinition(state.inputDefinitions, command.actor, command.inputId)) return state;
    const defaults = inputDefaults(command.kind);
    return {
      ...state,
      inputDefinitions: {
        ...state.inputDefinitions,
        [command.actor]: [
          ...state.inputDefinitions[command.actor],
          {
            ...defaults,
            id: command.inputId,
            label: uniqueLabel(defaults.label, state.inputDefinitions),
          },
        ],
      },
    };
  }
  if (command.type === "updateInput") {
    const definition = findInputDefinition(state.inputDefinitions, command.actor, command.inputId);
    if (!definition) return state;
    return { ...state, inputDefinitions: { ...state.inputDefinitions, [command.actor]: state.inputDefinitions[command.actor].map((item) => item.id === command.inputId ? { ...item, ...command.update } : item) } };
  }
  if (command.type === "renameInput") {
    const definition = findInputDefinition(state.inputDefinitions, command.actor, command.inputId);
    if (!definition) return state;
    const nextId = uniqueInputId(command.label, state.inputDefinitions, {actor: command.actor, inputId: definition.id});
    return {
      data: mapEditorBlocks(state.data, (block) => renameReferences(block, command.actor, definition.id, nextId)),
      inputDefinitions: { ...state.inputDefinitions, [command.actor]: state.inputDefinitions[command.actor].map((item) => item.id === definition.id ? { ...item, label: command.label, id: nextId } : item) },
    };
  }
  if (command.type !== "moveInput") return state;
  const definition = findInputDefinition(state.inputDefinitions, command.input.actor, command.input.inputId);
  if (!definition) return state;
  if (command.input.actor === command.actor) return state;
  const nextId = uniqueInputId(definition.label, state.inputDefinitions, command.input);
  return {
    data: mapEditorBlocks(state.data, (block) => renameReferences(block, command.input.actor, definition.id, nextId, command.actor)),
    inputDefinitions: {
      ...state.inputDefinitions,
      [command.input.actor]: state.inputDefinitions[command.input.actor].filter((item) => item.id !== definition.id),
      [command.actor]: state.inputDefinitions[command.actor].concat({ ...definition, id: nextId }),
    },
  };
};
